import { useEffect, useMemo, useState } from "react";

import { faBullseye, faLock } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { zodResolver } from "@hookform/resolvers/zod";
import { Divider, Grid2, Stack, TextField, Typography } from "@mui/material";
import { isFuture, parseISO } from "date-fns";
import { Controller, FormProvider, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import styled from "styled-components";

import { useGetEnrollmentGoals, usePutEnrollmentGoal } from "api/services/admin/hooks";
import { type EnrollmentGoalPayload, EnrollmentGoalPayloadSchema, type Professional } from "api/services/admin/schemas";
import { BaseButton } from "shared/atoms/BaseButton";
import { MUISelect } from "shared/atoms/MUI";
import { Tooltip } from "shared/atoms/Tooltip";
import { WidgetBox } from "shared/containers/WidgetBox";
import { GREEN, PURPLE, TEAL } from "theme/colors";
import { convertCentsToDollarsNumber } from "utils/convertCentsToDollars";
import { convertDollarsToCents, getCurrencySymbol } from "utils/currency";
import { reportError } from "utils/errorReporting";
import { translateCurrency } from "utils/translations";

import { EnrollmentGoalsSkeleton } from "./EnrollmentGoalsSkeleton";
import { SummaryBox } from "./SummaryBox";
import { useMonthOptions } from "./useMonthOptions";
import { NEW_ENROLLMENT_GOAL, defaultValues } from "./utils";

interface EnrollmentGoalsProps {
  hcoID: number;
  professionals: Professional[];
}

export const EnrollmentGoals: React.FC<EnrollmentGoalsProps> = ({ hcoID, professionals }) => {
  const { t } = useTranslation();
  const putEnrollmentGoal = usePutEnrollmentGoal();

  const {
    data: enrollmentGoalsData,
    isLoading,
    refetch,
  } = useGetEnrollmentGoals({
    health_care_organization_id: hcoID,
  });
  const data = enrollmentGoalsData?.items || [];
  const { monthOptions } = useMonthOptions({ data });

  const [showSaved, setShowSaved] = useState(false);
  const [selectedMonth, setSelectedMonth] = useState<number | "">("");
  const selectedGoal = data.find(item => item.id === selectedMonth);

  useEffect(() => {
    setSelectedMonth(data.find(item => item.is_current)?.id ?? data[data.length - 1]?.id ?? "");
  }, [data]);

  const form = useForm<EnrollmentGoalPayload>({
    mode: "onSubmit",
    reValidateMode: "onChange",
    defaultValues: {
      bonus_cents: selectedGoal?.bonus_cents ?? defaultValues.bonus_cents,
      bonus_currency: selectedGoal?.bonus_currency ?? defaultValues.bonus_currency,
      enrollment_goal: selectedGoal?.enrollment_goal ?? defaultValues.enrollment_goal,
    },
    resolver: zodResolver(EnrollmentGoalPayloadSchema),
  });

  const onSubmit = form.handleSubmit(async formData => {
    try {
      await putEnrollmentGoal.mutateAsync({
        health_care_organization_id: hcoID,
        payload: formData,
      });
      await refetch();
      form.reset(formData);
      setShowSaved(true);
      setTimeout(() => setShowSaved(false), 3000);
    } catch (error) {
      reportError("EnrollmentGoals: onSubmit failed", new Error(JSON.stringify({ error, variables: formData })));
    }
  });

  useEffect(() => {
    setShowSaved(false);
    if (selectedGoal) {
      form.reset({
        bonus_cents: selectedGoal.bonus_cents,
        bonus_currency: selectedGoal.bonus_currency,
        enrollment_goal: selectedGoal.enrollment_goal,
      });
    } else {
      form.reset(defaultValues);
    }
  }, [selectedMonth]);

  const isLocked =
    selectedMonth !== NEW_ENROLLMENT_GOAL && (selectedGoal == null || !isFuture(parseISO(selectedGoal.effective_from)));

  const activeProfessionals = useMemo(
    () => professionals.filter(professional => professional.status === "active"),
    [professionals]
  );
  const totalActiveProfessionals = activeProfessionals.length;

  const {
    bonus_cents: watchedBonusCents,
    bonus_currency: watchedBonusCurrency,
    enrollment_goal: watchedEnrollmentGoal,
  } = form.watch();

  const totalEnrollmentGoal = totalActiveProfessionals * watchedEnrollmentGoal;
  const totalBonus = translateCurrency(convertCentsToDollarsNumber(watchedBonusCents), watchedBonusCurrency);
  const totalBonusPayout = translateCurrency(
    convertCentsToDollarsNumber(totalActiveProfessionals * watchedBonusCents),
    watchedBonusCurrency
  );

  return (
    <WidgetBox
      title={t("admin.enrollment_goals.title")}
      icon={<FontAwesomeIcon icon={faBullseye} />}
      toolbar={
        <Stack direction="row" spacing={1} alignItems="center">
          {form.formState.isDirty && (
            <>
              <BaseButton
                type="submit"
                form="enrollment-goals-form"
                disabled={putEnrollmentGoal.isLoading}
                text={t("buttons.save")}
                size="small"
                square
                style={{ flexBasis: "auto" }}
              />
              <BaseButton
                text={t("buttons.discard")}
                variant="tertiary"
                disabled={putEnrollmentGoal.isLoading}
                onClick={() => form.reset()}
                size="small"
                square
                style={{ flexBasis: "auto" }}
              />
            </>
          )}

          {!form.formState.isDirty && showSaved && (
            <SummaryBox backgroundColor={GREEN[10]}>
              <Typography variant="subtitle2" color={GREEN[100]}>
                {t("buttons.saved")}
              </Typography>
            </SummaryBox>
          )}

          {isLocked && (
            <Tooltip title={t("admin.enrollment_goals.locked_tooltip")} arrow placement="top">
              <LockedIconContainer>
                <FontAwesomeIcon icon={faLock} />
              </LockedIconContainer>
            </Tooltip>
          )}

          <MUISelect
            disabled={isLoading}
            options={monthOptions}
            value={selectedMonth}
            onChange={event => {
              setSelectedMonth(event.target.value as number);
            }}
            size="small"
            sx={{ minWidth: 150 }}
          />
        </Stack>
      }
      color="green"
      skipPadding
    >
      {isLoading && <EnrollmentGoalsSkeleton />}
      {!isLoading && (
        <FormProvider {...form}>
          <form id="enrollment-goals-form" onSubmit={onSubmit} noValidate>
            <Stack spacing={3}>
              <Stack spacing={2}>
                <Typography variant="subtitle2" color="text.secondary">
                  {t("admin.enrollment_goals.form.enrollment_goal.title")}
                </Typography>
                <Stack direction="row" spacing={2} alignItems="center">
                  <Controller
                    name="enrollment_goal"
                    control={form.control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        disabled={isLocked}
                        type="text"
                        label={t("admin.enrollment_goals.form.enrollment_goal.label")}
                        onFocus={event => event.target.select()}
                        onChange={event => {
                          const strippedInputValue = event.target.value.replace(/\D/g, "");
                          field.onChange(strippedInputValue === "" ? 0 : parseInt(strippedInputValue, 10));
                        }}
                        error={!!form.formState.errors.enrollment_goal}
                        helperText={!!form.formState.errors.enrollment_goal && t("errors.field.required")}
                        slotProps={{
                          input: { inputMode: "numeric" },
                        }}
                        size="small"
                        fullWidth
                      />
                    )}
                  />

                  <SummaryBox>
                    <Stack direction="row" spacing={0.5} alignItems="center">
                      <Typography variant="subtitle2">{totalActiveProfessionals}</Typography>
                      <Typography variant="label">{t("common.clinicians")}</Typography>
                    </Stack>
                  </SummaryBox>

                  <SummaryBox backgroundColor={GREEN[10]}>
                    <Stack direction="row" spacing={0.5} alignItems="center">
                      <Typography variant="subtitle2" color={GREEN[100]}>
                        {totalEnrollmentGoal}
                      </Typography>
                      <Typography variant="label" color={GREEN[100]}>
                        {t("common.total")}
                      </Typography>
                    </Stack>
                  </SummaryBox>
                </Stack>
              </Stack>

              <Divider />

              <Stack spacing={2}>
                <Typography variant="subtitle2" color="text.secondary">
                  {t("admin.enrollment_goals.form.bonus_cents.title")}
                </Typography>
                <Controller
                  name="bonus_cents"
                  control={form.control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      type="text"
                      disabled={isLocked}
                      label={t("admin.enrollment_goals.form.bonus_cents.label", {
                        currency: getCurrencySymbol(watchedBonusCurrency),
                      })}
                      value={convertCentsToDollarsNumber(field.value)}
                      onFocus={event => event.target.select()}
                      onChange={event => {
                        const strippedInputValue = event.target.value.replace(/\D/g, "");
                        field.onChange(
                          strippedInputValue === ""
                            ? 0
                            : convertDollarsToCents({
                                dollars: strippedInputValue,
                                returnType: "number",
                              })
                        );
                      }}
                      error={!!form.formState.errors.bonus_cents}
                      helperText={!!form.formState.errors.bonus_cents && t("errors.field.required")}
                      size="small"
                      fullWidth
                    />
                  )}
                />
              </Stack>

              <Divider />

              <Grid2 container spacing={2} columns={12}>
                <Grid2 size={{ xs: 12, md: 6 }}>
                  <SummaryBox
                    label={t("admin.enrollment_goals.form.summary.enrollment_goal")}
                    value={watchedEnrollmentGoal}
                  />
                </Grid2>

                <Grid2 size={{ xs: 12, md: 6 }}>
                  <SummaryBox
                    label={t("admin.enrollment_goals.form.summary.total_enrollment_goal")}
                    value={totalEnrollmentGoal}
                    valueColor={GREEN[100]}
                  />
                </Grid2>

                <Grid2 size={{ xs: 12, md: 6 }}>
                  <SummaryBox
                    label={t("admin.enrollment_goals.form.summary.bonus_cents")}
                    value={totalBonus}
                    valueColor={PURPLE[100]}
                  />
                </Grid2>

                <Grid2 size={{ xs: 12, md: 6 }}>
                  <SummaryBox
                    label={t("admin.enrollment_goals.form.summary.total_bonus_cents")}
                    value={totalBonusPayout}
                    valueColor={TEAL[100]}
                  />
                </Grid2>

                <Grid2 size={{ xs: 12 }}>
                  <SummaryBox label={t("admin.enrollment_goals.form.summary.note")} />
                </Grid2>
              </Grid2>
            </Stack>
          </form>
        </FormProvider>
      )}
    </WidgetBox>
  );
};

const LockedIconContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  font-size: 14px;
  color: ${({ theme }) => theme.colors.redesign.db40};
  background: ${({ theme }) => theme.colors.redesign.db10};
  border-radius: 8px;
  cursor: help;
`;
